import { useState, useEffect } from 'react';
import {
  TextField, Autocomplete, Grid, Button, List, ListItem, ListItemText,
  Typography, IconButton,
  FormGroup
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

export default function ScenarioEventsForm({ data, onChange }) {
  // keep a single source of truth
  const [events, setEvents] = useState(data?.events || []);

  // form fields
  const [eventName, setEventName] = useState('');
  const [eventTrigger, setEventTrigger] = useState(null); // use null for Autocomplete
  const [eventTime, setEventTime] = useState('');         // empty string works best for number TextField
  const [eventTimeAuto, setEventTimeAuto] = useState(false)
  const [eventAction, setEventAction] = useState([]);     // multiple Autocomplete expects array
  const [activeTrigger, setActiveTrigger] = useState('');

  const triggerTypes = ['Manual trigger', 'Time', 'Participant action'];
  const responseTypes = ['Send reminder (facilitator)', 'Send message (participant)', 'Send bloods', 'Send obs', 'Send imaging'];

  const normalize = (d) => (Array.isArray(d) ? d : d?.events || []);

  // if parent replaces the array (e.g., hydration), sync local state
  useEffect(() => {
    setEvents(normalize(data));
  }, [data]);

  // only depend on `events` to avoid the loop
  useEffect(() => {
    onChange?.(events);           // send just the array, keep it simple
  }, [events]);  

  const resetForm = () => {
    setEventName('');
    setEventTrigger(null);
    setActiveTrigger('');
    setEventTime('');
    setEventAction([]);
  };

  const addEvent = () => {
    const name = eventName.trim();
    if (!name) return alert('Event name is required.');
    if (!eventTrigger) return alert('Please choose an event trigger.');
    if (eventTrigger === 'Time' && (eventTime === '' || Number.isNaN(Number(eventTime)))) {
      return alert('Please enter a valid time in minutes.');
    }

    const newEvent = {
      id: crypto.randomUUID?.() ?? `evt_${Date.now()}`,
      eventName: name,
      eventTrigger,
      eventTime: eventTrigger === 'Time' ? Number(eventTime) : null,
      eventAction: [...eventAction], // copy
    };

    setEvents(prev => [...prev, newEvent]);
    resetForm(); // <-- clear inputs after save
  };

  return (
    <div>
      Add key scenario events here. Events can be triggered from the facilitator dashboard or automatically on specified conditions. All events and their times will be printed to the final sim scenario summary.

      <div style={{ marginTop: '1rem' }}>
        <Grid container spacing={2} alignItems="center" wrap="nowrap" style={{ marginBottom: '1rem' }}>
          <Grid item style={{ flexBasis: '30%' }}>
            <TextField
              fullWidth
              label="Event Name"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
            />
          </Grid>

          <Grid item style={{ flexBasis: '35%' }}>
            <Autocomplete
              options={triggerTypes}
              value={eventTrigger}
              onChange={(_, newValue) => {
                setEventTrigger(newValue);
                setActiveTrigger(newValue ?? '');
                if (newValue !== 'Time') setEventTime(''); // clear time if no longer time-based
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Event Trigger" placeholder="Search triggers..." />
              )}
            />

            {activeTrigger === 'Time' && (
              <div style={{display:'flex', flexDirection:'row', gap:'1rem'}}>
              <TextField
                fullWidth
                label="Event Time (mins)"
                type="number"
                value={eventTime}
                onChange={(e) => setEventTime(e.target.value)}
                style={{ marginTop: '0.5rem', width: '50%' }}
              />
              <FormControlLabel control={<Switch />} label="Auto trigger?" />
              </div>
            )}
          </Grid>

          <Grid item style={{ flexBasis: '35%' }}>
            <Autocomplete
              multiple
              options={responseTypes}
              value={eventAction}
              onChange={(_, newValue) => setEventAction(newValue ?? [])}
              renderInput={(params) => (
                <TextField {...params} label="Select Event Actions" placeholder="Search actions..." />
              )}
            />
          </Grid>

          <Grid item style={{ flexBasis: '15%' }}>
            <Button variant="contained" fullWidth onClick={addEvent}>
              Add
            </Button>
          </Grid>
        </Grid>

        <List>
          {events.map((item, index) => (
            <ListItem
              key={item.id ?? index}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => setEvents(prev => prev.filter((_, i) => i !== index))}
                >
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText
                primary={[
                  item.eventName,
                  item.eventTrigger,
                  item.eventTrigger === 'Time' && item.eventTime != null ? `${item.eventTime} min` : null,
                  (item.eventAction || []).join(', ')
                ].filter(Boolean).join(' — ')}
              />
            </ListItem>
          ))}
        </List>
      </div>
    </div>
  );
}
